import React, { useState } from "react";
import { useStripe, useElements, PaymentElement } from "@stripe/react-stripe-js";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { postPublicRemovalOrderPayment } from "components/proejctsandquoteapicalls";
import { buildPublicPaymentPaidBy, type PublicPaymentActor } from "components/publicpaymnts";

export type RemovalPublicPaymentsProps = {
  supplierId: string;
  emailBrandingSupplierId?: string;
  totalAmount: number;
  dueAmount: number;
  orderId: string;
  projectId?: string;
  publicToken?: string;
  selectedPaymentMethod: string;
  createdBy?: PublicPaymentActor;
  paidBy?: PublicPaymentActor;
  onPaymentSuccess: (paymentIntentId: string) => void;
};

/** POST `/public/removal_orders/{order_id}/payment?token=` after Stripe succeeds. */
const RemovalPublicPayments: React.FC<RemovalPublicPaymentsProps> = ({
  supplierId,
  emailBrandingSupplierId,
  totalAmount,
  dueAmount,
  orderId,
  projectId,
  publicToken,
  selectedPaymentMethod,
  createdBy,
  paidBy,
  onPaymentSuccess,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const paymentElementScrollStyle: React.CSSProperties = {
    maxHeight: "min(52vh, 420px)",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    paddingRight: 4,
  };

  const handlePaymentSuccess = async (paymentIntentId: string) => {
    try {
      const response = await postPublicRemovalOrderPayment(projectId || "", orderId, publicToken || "", {
        payment_id: paymentIntentId,
        total_amount: Math.round(Number(totalAmount) * 100) / 100,
        amount_paid: Math.round(Number(totalAmount) * 100) / 100,
        amount_due: dueAmount,
        payment_date: new Date().toISOString(),
        method: selectedPaymentMethod,
        paid_by: buildPublicPaymentPaidBy(paidBy, createdBy, supplierId),
      });

      if (!response.ok) {
        throw new Error("Failed to record removal payment");
      }

      await response.json().catch(() => ({}));
      localStorage.removeItem("cart_id");

      onPaymentSuccess(paymentIntentId);
    } catch (error) {
      const errMessage = (error as Error).message || "An unknown error occurred";
      console.error("Removal payment update failed:", errMessage);
      toast.error(`Error: ${errMessage}`);
    } finally {
      // navigate(0);
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) {
      toast.error("Stripe is not ready");
      return;
    }

    setLoading(true);
    try {
      await elements.submit();
      const paymentIntentRes = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (paymentIntentRes.error) {
        throw new Error(paymentIntentRes.error.message || "Payment failed");
      }

      const pi = paymentIntentRes.paymentIntent;
      if (!pi?.id) {
        throw new Error("Payment did not complete");
      }

      await handlePaymentSuccess(pi.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Payment failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <ToastContainer />
      <form onSubmit={handleSubmit}>
        <div className="mb-3" style={paymentElementScrollStyle}>
          <PaymentElement
            options={{
              layout: {
                type: "tabs",
                defaultCollapsed: false,
              },
            }}
          />
        </div>
        <button type="submit" className="btn btn-primary w-100 fw-bold" disabled={!stripe || loading}>
          {loading ? "Processing..." : `Pay $${totalAmount.toFixed(2)}`}
        </button>
      </form>
    </div>
  );
};

export default RemovalPublicPayments;
